import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, ConfigProvider, Drawer, Grid, Modal, Space, Tabs, Tooltip, message } from 'antd';
import type { TableProps } from 'antd';
import {
  BarChartOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Permissions } from '@tcr-erp/shared';
import { PageShell } from '@/components/PageShell/PageShell';
import { ERPTable } from '@/components/DataTable/ERPTable';
import { FilterBar } from '@/components/FilterBar/FilterBar';
import { useAuth } from '@/hooks/useAuth';
import { useRbac } from '@/hooks/useRbac';
import { useStores } from '@/hooks/useStoreSettings';
import { useListPageState } from '@/hooks/useListPageState';
import { useOrderList } from '@/hooks/useOrderList';
import { useAllStaffUsers } from '@/hooks/useStaffList';
import { useCancelOrder, useConfirmOrder, useSkipOrder, useUnskipOrder } from '@/hooks/useOrderMutations';
import {
  offPlatformFulfillmentApi,
  useCreateOffPlatformExportSlips,
  useMarkOffPlatformPickedUp,
} from '@/api/orders/offplatform-fulfillment';
import { useQueryClient } from '@tanstack/react-query';
import { b2bReceivableKeys, orderKeys } from '@/services/queryKeys';
import { b2bReceivableService } from '@/services/b2b-receivable.service';
import { computeOrderCreditLimitInfo } from './components/useOrderCreditLimitWarning';
import { orderService } from '@/services/order.service';
import { getApiError } from '@/utils/apiError';
import type { TenantOrder } from '@/mocks/orders';
import { ROUTES } from '@/constants/routes';
import { useOrderColumns } from './parts/useOrderColumns';
import { buildMultiOrderSections, buildSingleOrderSections, type OrderPanelContext } from './parts/orderPanelActions';
import {
  buildConfirmOrderPrompt,
  isGtPrepaidOrder,
  ORDERS_CONFIRM_GT_PREPAID,
} from './components/confirmOrderPrompt';
import {
  buildPickupOrderConfirmVariables,
  canConfirmPickupOrder,
  confirmOrderAndRefreshStatus,
  getOrderOutstandingDebt,
  isPickupHandoverOnConfirm,
  PickupOrderConfirmationContent,
  refetchConfirmedOrderDetail,
} from './components/pickupOrderConfirmation';
import { buildOrderSortParam, resolveOrderSort } from './parts/orders-sort';
import { ORDER_STATUS_GROUP_OPTIONS } from './parts/constants';
import { buildOrderStatusTabLabel } from './parts/status-tab-label';
import {
  buildOrderAdvancedFilters,
  buildOrderApiFilters,
  buildOrderPrimaryFilters,
  normalizeOrderDateUrlValue,
  orderDateUrlToApiDate,
  ORDER_DATE_PRESET_DEFAULT,
  ORDER_FILTER_KEYS,
  parseOrderAmount,
  resolveOrderDateRange,
  type OrderFilterKey,
  type OrderFilterPatch,
} from './parts/filters';
import { creditCheckCustomerIds, excludedReasons, planBatchOrderActions } from './utils/batchOrderActions';
import { errorMessageOf, formatBatchFailures, packingResultFailures, runBatch } from './utils/runBatch';
import { splitConfirmTargets } from './utils/confirmTargets';
import { planRowMenuExtras } from './utils/rowMenuBatch';
import { effectiveSelection, rememberVisibleSelection, type SelectionMemo } from './utils/selectionScope';
import type { BatchActionPlan } from './utils/batchOrderActions';
import { PosBatchActionPanel } from './parts/PosBatchActionPanel';
import { BatchConfirmContent } from './parts/BatchConfirmContent';
import { BulkOrderTagModal } from './parts/BulkOrderTagModal';
import OrderCodeSelectionHeader from '@/pages/ECom/orders/parts/OrderCodeSelectionHeader';
import { useAssignOrderTags } from '@/api/orders/order-tags';
import { OrderExcelButton } from './components/OrderExcelButton';
import { ImportOrderModal } from './components/ImportOrderModal';
import { OrderSummaryModal } from './components/OrderSummaryModal';
import { PosErpSkuStockModal } from './components/PosErpSkuStockModal';
import { OrderTagModal } from './parts/OrderTagModal';
import { printOrderReceipt } from './utils/printReceipt';
import { SendToCustomerModal } from './parts/SendToCustomerModal';
import { copyOrderToPos } from './utils/copyOrderToPos';
import { resolvePrintSlipMode } from './utils/orderPrintSlip';
import { useDownloadShippingLabel } from '@/hooks/useShipping';
import { useB2BCreditLimitsOf } from '@/hooks/useB2BReceivables';
import { printPdfBlob } from '@/utils/printPdf';
import { openExportSlipPrintPreview } from '@/utils/printExportSlip';
import type { ExportSlipPrintOrder } from '@/utils/printExportSlip';
import { OrderShiftSummary } from './components/OrderShiftSummary';
import { OrderFilterDrawer } from './components/OrderFilterDrawer';
import { countActiveOrderFilters } from './components/ActiveFilterTags';
import { userRolesOf } from '@/utils/userRoles';

/**
 * TCR-1788 — công tắc hiển thị nút "Tổng kết ca" trên hàng toolbar màn Đơn hàng.
 * Đang tắt theo yêu cầu tester (chức năng trùng với POS → Ca làm việc). Đổi thành `true`
 * là nút hiện lại nguyên vẹn — Drawer "Tổng kết ca hiện tại" và `OrderShiftSummary` vẫn
 * được giữ trong file này, không cần dựng lại.
 */
const SHOW_SHIFT_SUMMARY_BUTTON = false;

/**
 * TCR-2491 — mật độ hiển thị bám màn Đơn ECom (`ECom/orders/index.tsx`): nút + bộ lọc cỡ small
 * nằm trên tabs, tabs nhỏ sát bảng, ô bảng padding 4px 6px + chữ 12px để thấy nhiều dòng hơn.
 * Scope theo className riêng, không đụng `.erp-table` của các màn khác.
 */
const ORDERS_LIST_CSS = `
  /* Như OrderListToolbar của ECom: cụm trái (nút + bộ lọc) tự xuống dòng, cụm Excel /
     "+ Tạo đơn" ghim góc phải hàng đầu. */
  .orders-list-toolbar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    padding-bottom: 6px;
  }
  .orders-list-toolbar__main {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;
  }
  /* FilterBar tự bọc padding 8px 0 — trong hàng công cụ thì thừa. */
  .orders-list-toolbar__filters > div {
    padding: 0 !important;
  }
  @media (max-width: 767px) {
    /* Màn hẹp: ghim góc phải làm Refresh/Bộ lọc/Cột dựng đứng 3 hàng — cho cụm Excel /
       "+ Tạo đơn" xuống hàng dưới thay vào đó. */
    .orders-list-toolbar {
      flex-wrap: wrap;
    }
    .orders-list-toolbar__main {
      flex: 1 1 auto;
    }
  }
  .orders-list-tabs .ant-tabs-nav {
    margin-bottom: 4px;
  }
  .orders-list-tabs .ant-tabs-tab {
    padding: 4px 0;
    font-size: 13px;
  }
  .orders-list-table .ant-table {
    font-size: 12px;
  }
  .orders-list-table .ant-table-cell {
    padding: 4px 6px !important;
    font-size: 12px;
    line-height: 1.25;
  }
  /* Typography tự đặt 14px, không ăn theo ô. */
  .orders-list-table .ant-typography {
    font-size: 12px;
  }
  /* Các ô xếp dọc (mã đơn + trạng thái, tên + SĐT khách, mã vận đơn…) dùng Space gap 8px —
     nén còn 2px là chỗ ăn chiều cao dòng nhiều nhất. */
  .orders-list-table .ant-table-tbody .ant-space-vertical {
    row-gap: 2px !important;
  }
  .orders-list-table .ant-table-thead > tr > th {
    padding: 5px 6px !important;
    font-weight: 600;
  }
  .orders-list-table .ant-tag {
    margin-inline-end: 2px;
    padding-inline: 5px;
    line-height: 18px;
    font-size: 11px;
  }
  .orders-list-table .ant-table-tbody > tr:nth-child(even) > td {
    background: #fafafa;
  }
  .orders-list-table .ant-table-tbody > tr:hover > td {
    background: #eef6ff !important;
  }
`;

interface OrdersPageProps {
  forcedUserType?: 'personal' | 'organization';
  pageKey?: string;
}

export function OrdersPage({
  forcedUserType,
  pageKey = 'orders-list',
}: OrdersPageProps = {}) {
  const { user } = useAuth();
  const { hasPermission } = useRbac();
  // TCR-1709 — `orders.cancel` nay là key thật gác `POST /orders/:id/cancel` (trước đây route
  // dùng `orders.update`, nên bỏ tick "Hủy đơn hàng" không có tác dụng gì).
  const canCancelOrders = hasPermission('orders.cancel');
  // TCR-1964 final review — "Huỷ nhiều" phải tự loại các đơn đã khoá theo trạng thái TRƯỚC khi
  // gọi API (xem `partitionOrdersForBulkCancel`), cùng quyền panel hành động dùng
  // (`panelContext` bên dưới).
  const canOverrideLockOrders = hasPermission(Permissions.ORDERS_OVERRIDE_LOCK);
  // TCR-1911 — nút "+ Tạo đơn" trước đây render vô điều kiện: role chỉ có `orders.view` (NV Đóng
  // hàng, thủ kho) vẫn thấy nút rồi bấm vào là bị PrivateRoute của /pos chặn. Gate theo đúng 2
  // điều kiện để thao tác chạy được tới cùng:
  //  - `pos.use`: `requiredPermissions` của chính route /pos (App.tsx) — nút này điều hướng sang đó;
  //  - `orders.create` HOẶC `pos.orders.create`: quyền tạo đơn, đúng cặp key route /ecom/orders/new
  //    đang dùng (TCR-1698 #4 đã ẩn `orders.create` khỏi role-editor nên buộc phải OR với key POS).
  const canCreateOrder = hasPermission('pos.use') && hasPermission(['orders.create', 'pos.orders.create']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    page,
    pageSize,
    sortField,
    sortOrder,
    setTableState,
    filters,
    setDraft,
    applyFilters,
    clearFilters,
    selectedRowKeys,
    setSelectedRowKeys,
    clearSelection,
  } = useListPageState<OrderFilterKey>({
    pageKey,
    filterKeys: ORDER_FILTER_KEYS,
    userId: user?.id,
    // Lưu preset thay vì ngày tuyệt đối: snapshot filter nằm ở localStorage nên ghi
    // ngày cứng sẽ khiến phiên kéo sang tháng sau vẫn lọc tháng cũ.
    defaultFilters: { date_preset: ORDER_DATE_PRESET_DEFAULT },
  });

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    (['date_from', 'date_to'] as const).forEach((key) => {
      const value = searchParams.get(key);
      const normalized = normalizeOrderDateUrlValue(value ?? undefined);
      if (value && normalized && value !== normalized) {
        next.set(key, normalized);
        changed = true;
      }
    });
    if (changed) setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * Instant-apply: mọi thay đổi filter (FilterBar, Tabs kênh, panel lọc trên header cột,
   * bấm x trên tag) commit thẳng lên URL. Không còn tầng draft treo, nên `applyFilters()`
   * không thể kéo theo điều kiện dở dang của control khác — đây là cách P1-1 được đóng.
   */
  const applyFilterPatch = useCallback(
    (patch: OrderFilterPatch) => {
      (Object.entries(patch) as [OrderFilterKey, string][]).forEach(([key, value]) => {
        setDraft(key, value);
      });
      applyFilters();
    },
    [setDraft, applyFilters],
  );

  const activeDateRange = resolveOrderDateRange(filters);
  const apiDateFrom = orderDateUrlToApiDate(activeDateRange.from);
  const apiDateTo = orderDateUrlToApiDate(activeDateRange.to);

  const activeSort = useMemo(() => resolveOrderSort(sortField, sortOrder), [sortField, sortOrder]);

  /**
   * TCR-1092 round 1 review fix: nguồn nhân viên cho filter "Người tạo đơn" phải là
   * TOÀN BỘ staff (không giới hạn 100 bản ghi), nếu không tenant có > 100 nhân viên
   * active sẽ có người không chọn/lọc được mà không có cảnh báo gì. Dùng
   * `useAllStaffUsers` (đã dùng ở `EmployeeDetail.tsx` cho ô "Chọn user ERP") — hook
   * này tự gọi nhiều trang rồi ghép lại (`staffService.listAll`), trả thẳng
   * `TenantStaff[]` (khác `useStaffList` trả `{ data, meta }`). Trang danh sách không
   * có 1 store cố định (khác `detail.tsx` truyền `order?.store?._id`) nên không lọc
   * theo `store_id` — lọc "người tạo đơn" cần thấy nhân viên của mọi chi nhánh.
   *
   * TCR-1956: hook trên gọi KHÔNG điều kiện khiến MỌI lần vào `/orders` đều bắn thêm
   * `GET /users` dù user chưa từng đụng tới filter này — tốn băng thông vô ích. Gate lại:
   * chỉ fetch khi drawer "Bộ lọc" đã từng mở trong phiên xem trang này (chốt bằng
   * `staffOptionsEverNeeded`, KHÔNG tắt lại khi đóng drawer — tránh gọi lại API mỗi lần
   * mở/đóng vì `staleTime` toàn cục = 0), hoặc khi URL đã có sẵn `created_by` (để
   * `ActiveFilterTags` hiện đúng tên thay vì rơi về hiện ID).
   */
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [staffOptionsEverNeeded, setStaffOptionsEverNeeded] = useState(false);
  useEffect(() => {
    if (filterDrawerOpen) setStaffOptionsEverNeeded(true);
  }, [filterDrawerOpen]);
  const needsStaffOptions = staffOptionsEverNeeded || Boolean(filters.created_by);
  const { data: allStaffUsers } = useAllStaffUsers(needsStaffOptions);
  const staffOptions = useMemo(
    () => (allStaffUsers ?? []).map((s) => ({ label: s.name, value: s.staff_id })),
    [allStaffUsers],
  );

  /**
   * TCR-1668 — nguồn cho filter "Chi nhánh". `useStores()` gọi `GET /stores?limit=200&state=1`
   * (`store.service.ts`), tức chỉ chi nhánh đang hoạt động và ĐÃ được BE lọc theo scope của
   * người đăng nhập (`resolveStoreScope`) — nhân viên một chi nhánh chỉ thấy chi nhánh của
   * mình trong dropdown, đúng thứ mong muốn, không cần lọc thêm ở FE.
   */
  const { data: storeList } = useStores();
  const storeOptions = useMemo(
    () => (storeList ?? []).map((st) => ({ label: st.name, value: st._id })),
    [storeList],
  );

  /**
   * Mobile (dưới breakpoint `md`): gộp nút "Bộ lọc" (filter nâng cao) và "Lọc" (search/kênh/
   * thời gian mà `FilterBar` tự render riêng khi hẹp) thành MỘT nút + MỘT drawer, và ẩn dòng
   * "Đang lọc" — trên màn hẹp 2 nút lọc cạnh nhau + dòng tag dài gây rối, số lượng lọc đã có
   * badge trên nút, còn xoá từng điều kiện vẫn làm được trong drawer.
   */
  const screens = Grid.useBreakpoint();
  const isMobileFilterLayout = !screens.md;
  const [openColumnSettings, setOpenColumnSettings] = useState<(() => void) | null>(null);
  const [shiftSummaryOpen, setShiftSummaryOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  /** TCR-1095: id đơn đang xem nhanh trong pop-up; `null` = pop-up đóng. */
  const [summaryOrderId, setSummaryOrderId] = useState<string | null>(null);
  /** TCR-2495: mã ERP đang xem tồn kho + kho xuất của đơn chứa mã đó; `null` = popup đóng. */
  const [erpSkuStock, setErpSkuStock] = useState<{ sku: string; warehouseIds: string[] } | null>(null);
  const cancelMutation = useCancelOrder();
  const confirmMutation = useConfirmOrder();
  const skipMutation = useSkipOrder();
  const unskipMutation = useUnskipOrder();
  const advancePackingMutation = useCreateOffPlatformExportSlips();
  const markPickedUpMutation = useMarkOffPlatformPickedUp();
  const downloadShippingLabelMutation = useDownloadShippingLabel();
  const queryClient = useQueryClient();
  /** TCR-1094: đơn đang mở modal Gán thẻ; `null` = đóng. */
  const [tagModalOrder, setTagModalOrder] = useState<TenantOrder | null>(null);
  const [sendToCustomerOrder, setSendToCustomerOrder] = useState<TenantOrder | null>(null);
  /** TCR-1094: chặn double-click "Sao chép đơn hàng" trong menu "..." khi đang tra biến thể. */
  const [isCopyingOrder, setIsCopyingOrder] = useState(false);

  /**
   * TCR-1092: 4 filter mới, chuẩn hoá MỘT lần rồi dùng cho CẢ query danh sách và
   * nút "Xuất Excel" — trước đây export không nhận 4 filter này nên file xuất ra
   * rộng hơn bảng người dùng đang nhìn.
   */
  const tcr1092ApiFilters = useMemo(
    () => buildOrderApiFilters(filters),
    [filters],
  );

  const { data, isFetching, isPlaceholderData, refetch } = useOrderList({
    page,
    page_size: pageSize,
    channel_group: filters.channel_group || undefined,
    status: filters.status || undefined,
    status_group: filters.status_group || undefined,
    user_type: forcedUserType,
    search: filters.search || undefined,
    order_number: filters.order_number || undefined,
    customer: filters.customer || undefined,
    created_at_from: apiDateFrom,
    created_at_to: apiDateTo,
    total_from: parseOrderAmount(filters.total_from),
    total_to: parseOrderAmount(filters.total_to),
    ...tcr1092ApiFilters,
    sort: buildOrderSortParam(activeSort),
  });
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  // TCR-2497 — vùng chọn giữ qua trang và qua bộ lọc (xoá tìm kiếm thì đơn bị ẩn hiện lại vẫn tích),
  // nhưng badge/panel/hành động chỉ tính đơn thuộc bộ lọc hiện tại: đang hiện trên trang, hoặc đã
  // tích ở trang khác của chính bộ lọc này. Trang và sort không đổi tập đơn nên không vào khoá.
  const selectionScope = useMemo(() => JSON.stringify({ ...filters, forcedUserType }), [filters, forcedUserType]);
  const [selectionMemo, setSelectionMemo] = useState<SelectionMemo>({});
  useEffect(() => {
    // `placeholderData` giữ danh sách của bộ lọc cũ trong lúc tải — chưa được ghi nhận cho bộ lọc mới.
    if (isPlaceholderData) return;
    setSelectionMemo((prev) => rememberVisibleSelection(prev, selectedRowKeys, data?.items ?? [], selectionScope, `${page}/${pageSize}`));
  }, [data, isPlaceholderData, selectedRowKeys, selectionScope, page, pageSize]);

  const handleCancelOrder = (row: TenantOrder) => {
    // TCR-1709 — nút "Huỷ" vẫn HIỆN khi thiếu quyền (khác 3 nút in bị ẩn hẳn): mô tả ticket
    // yêu cầu đích danh "click vào button Huỷ đơn hiển thị thông báo → ko có quyền huỷ đơn
    // hàng. Vui lòng liên hệ với quản lý". Chặn ở đây, trước khi mở modal nhập lý do, để nhân
    // viên không gõ xong lý do rồi mới ăn 403 từ BE.
    if (!canCancelOrders) {
      message.error('Bạn không có quyền huỷ đơn hàng. Vui lòng liên hệ với quản lý.');
      return;
    }
    let reason = '';
    Modal.confirm({
      title: `Huỷ đơn ${row.order_number}?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <textarea
          placeholder="Lý do huỷ (bắt buộc)"
          rows={3}
          style={{ width: '100%', padding: 8, marginTop: 8, borderRadius: 6, border: '1px solid #d9d9d9' }}
          onChange={(e) => {
            reason = e.target.value;
          }}
        />
      ),
      okText: 'Xác nhận huỷ',
      okButtonProps: { danger: true },
      cancelText: 'Thoát',
      onOk: async () => {
        if (!reason.trim()) {
          message.error('Vui lòng nhập lý do huỷ');
          throw new Error('reason required');
        }
        try {
          await cancelMutation.mutateAsync({ id: row._id, reason: reason.trim() });
          message.success('Đã huỷ đơn');
        } catch (e: unknown) {
          // TCR-1964 final review (2) — cùng pattern `detail.tsx`: đọc message thật từ BE (vd 400
          // `ORDER_STATUS_LOCKED`) thay vì luôn hiện chữ cố định, người dùng không biết vì sao.
          message.error(getApiError(e)?.message ?? 'Huỷ đơn thất bại');
        }
      },
    });
  };

  /**
   * TCR-1964 final review (1) — đơn khoá theo trạng thái được lọc ra trước (kế hoạch `cancel` của
   * `planBatchOrderActions`), không gửi lên BE. TCR-2264 (QA 13/10) — dùng chung cho panel hàng loạt
   * và mục "Huỷ (k)" ở menu ⋮ khi đang chọn nhiều đơn; tổng kết đơn lỗi kèm lý do thật từ BE.
   */
  const handleBulkCancel = () => {
    if (!canCancelOrders) {
      message.error('Bạn không có quyền huỷ đơn hàng. Vui lòng liên hệ với quản lý.');
      return;
    }
    const plan = batchPlans.cancel;
    if (plan.eligible.length === 0) {
      message.warning(`Không có đơn nào huỷ được: ${excludedReasons(plan) ?? ''}`);
      return;
    }
    let reason = '';
    Modal.confirm({
      title: `Huỷ ${plan.eligible.length} đơn được chọn?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <div style={{ marginTop: 4, color: '#595959' }}>{plan.eligible.map((o) => o.order_number).join(', ')}</div>
          <textarea
            placeholder="Lý do huỷ áp dụng cho tất cả đơn (bắt buộc)"
            rows={3}
            style={{ width: '100%', padding: 8, marginTop: 8, borderRadius: 6, border: '1px solid #d9d9d9' }}
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
          {excludedNote(plan.excluded)}
        </div>
      ),
      okText: 'Xác nhận huỷ',
      okButtonProps: { danger: true },
      cancelText: 'Thoát',
      onOk: async () => {
        if (!reason.trim()) {
          message.error('Vui lòng nhập lý do huỷ');
          throw new Error('reason required');
        }
        setBatchBusy(true);
        try {
          finishBatch('Huỷ', await runBatch(plan.eligible, 5, (o) => o.order_number, (o) =>
            cancelMutation.mutateAsync({ id: o._id, reason: reason.trim() })));
        } finally {
          setBatchBusy(false);
        }
      },
    });
  };

  const handlePrintReceipt = async (
    row: TenantOrder,
    kind: 'receipt' | 'invoice' = 'receipt',
  ) => {
    const labels: Record<typeof kind, string> = {
      receipt: 'hoá đơn POS',
      invoice: 'hoá đơn VAT',
    };
    try {
      const res = await printOrderReceipt(
        row,
        {
          storeName: row.store?.name,
          storeAddress: row.store?.address,
        },
        kind,
      );
      if (res && res.method !== 'browser') {
        message.success(`Đã gửi lệnh in ${labels[kind]}`);
      }
    } catch (e: unknown) {
      const err = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      message.error(err ?? (e as Error)?.message ?? `In ${labels[kind]} thất bại`);
    }
  };

  /** Chuyển `TenantOrder` 1 dòng sang input của `openExportSlipPrintPreview` (TCR-1094). */
  const toExportSlipPrintOrder = (row: TenantOrder): ExportSlipPrintOrder => ({
    order_number: row.order_number,
    items: (row.items ?? []).map((item) => ({
      sku: item.sku,
      name: item.product_name,
      quantity: item.qty,
      image_url: item.image_url,
    })),
  });

  /**
   * TCR-1094 (comment `33057`): "In phiếu" — đơn có hãng vận chuyển thật đã có
   * vận đơn thì in nhãn hãng (tái dùng đúng cơ chế `Shipping/detail.tsx`
   * `handlePrintLabel`); còn lại (MANUAL) in phiếu xuất hàng dựng từ dữ liệu
   * đơn sẵn có trên dòng bảng — KHÔNG gọi `createExportSlips` (mutation state
   * riêng của nút "Đang đóng gói", chỉ khớp đúng 1 nhóm, gọi lại ở đây sẽ tái
   * diễn đúng bug fake-success đã bị QA bắt lỗi trước đó). Nút đã disabled ở
   * `orderActionColumn.tsx` cho case "hãng thật nhưng chưa đủ điều kiện" nên
   * `mode.kind === 'unavailable'` ở đây không nên xảy ra qua click thật.
   */
  const handlePrintSlip = async (row: TenantOrder) => {
    const mode = resolvePrintSlipMode(row);
    if (mode.kind === 'unavailable') return;
    if (mode.kind === 'export-slip') {
      openExportSlipPrintPreview([toExportSlipPrintOrder(row)], undefined, row.order_number);
      return;
    }
    const label = await downloadShippingLabelMutation.mutateAsync(mode.shippingOrderId).catch(() => null);
    if (!label) return;
    const { printed } = await printPdfBlob(label.blob, label.filename);
    if (!printed) {
      message.info('Trình duyệt không mở được hộp thoại in — đã tải nhãn về máy');
    }
  };

  /**
   * TCR-1094: bản danh sách của "Sao chép đơn hàng" — dùng lại ĐÚNG lõi
   * `copyOrderToPos` (lookup biến thể qua `buildCopyOrderItems` + mồi POS qua
   * `mapOrderToPosPrefill`) mà `detail.tsx`'s `handleCopyOrder` dùng, chỉ khác
   * cách hiện trạng thái đang tải: dùng `message.loading` thay cho spinner trên nút.
   */
  const handleCopyOrder = async (row: TenantOrder) => {
    if (isCopyingOrder) return;
    setIsCopyingOrder(true);
    const hideLoading = message.loading('Đang chuẩn bị sao chép đơn hàng...', 0);
    try {
      await copyOrderToPos(row, navigate);
    } finally {
      hideLoading();
      setIsCopyingOrder(false);
    }
  };

  /** TCR-1094: placeholder giống hệt `handleReturnOrder` ở `detail.tsx` — chưa có luồng đổi trả thật. */
  const handleReturnOrder = () => {
    Modal.info({
      title: 'Đổi trả hàng',
      content: 'Tính năng đổi trả hàng đang được phát triển. Vui lòng xử lý thủ công và liên hệ kế toán để ghi nhận đổi trả.',
      okText: 'Đã hiểu',
    });
  };

  /** TCR-1094: nhóm "Chuyển trạng thái" — Xác nhận (Chờ xác nhận → Mới). */
  const handleConfirmOrder = async (row: TenantOrder, debtAcknowledged: boolean) => {
    try {
      const isPickup = isPickupHandoverOnConfirm(row);
      const { refreshedOrder, refreshFailed } = await confirmOrderAndRefreshStatus(
        isPickup,
        () => confirmMutation.mutateAsync(buildPickupOrderConfirmVariables(row, debtAcknowledged)),
        () => refetchConfirmedOrderDetail(queryClient, row._id),
      );
      if (!isPickup) {
        message.success('Đã xác nhận đơn');
      } else if (refreshFailed) {
        message.success('Đã Duyệt đơn và ghi nhận khách đã nhận hàng');
        message.warning('Chưa thể tải trạng thái hạch toán mới nhất. Vui lòng mở lại chi tiết đơn để kiểm tra.');
      } else if (refreshedOrder?.pos_fulfillment?.accounting_status === 'failed') {
        message.success('Đã Duyệt đơn và ghi nhận khách đã nhận hàng');
        message.warning('Hạch toán kế toán thất bại. Có thể thử lại từ chi tiết đơn.');
      } else {
        message.success('Đã Duyệt đơn và ghi nhận khách đã nhận hàng');
      }
    } catch (e: unknown) {
      // BE trả lý do ở `response.data.error.message` — đọc qua getApiError để toast nêu đúng nguyên nhân (TCR-2264 QA 13/10).
      message.error(getApiError(e)?.message ?? 'Xác nhận đơn thất bại');
    }
  };

  /**
   * TCR-1094 final fix wave (Fix 3): không có transition ngược lại (un-confirm,
   * ngoài phạm vi — xem spec), nên bấm nhầm không thể sửa. Thêm `Modal.confirm`
   * trước khi gọi mutation, khớp quy tắc đã ghi trong design spec + cùng pattern
   * `handleCancelOrder`/`handleAdvanceToPacking`/`handleMarkPickedUp` trong file này.
   */
  const confirmConfirmOrder = async (row: TenantOrder) => {
    // TCR-2439 v3 — cùng số liệu với nút "Duyệt đơn" ở trang chi tiết: hạn mức HIỆN TẠI của khách, vì
    // cờ lưu lúc tạo đơn có thể đã lỗi thời. Không đọc được (không có quyền xem công nợ…) ⇒ theo cờ.
    const creditLimit = row.user_type === 'organization' && row.user_id && !isGtPrepaidOrder(row)
      ? await queryClient.fetchQuery({
        queryKey: b2bReceivableKeys.creditLimit(String(row.user_id)),
        queryFn: () => b2bReceivableService.creditLimit(String(row.user_id)),
        staleTime: 0,
      }).catch(() => null)
      : null;
    const prompt = buildConfirmOrderPrompt({
      creditApprovalStatus: row.credit_approval_status,
      creditApprovalReason: row.credit_approval_reason,
      creditWarning: computeOrderCreditLimitInfo(row, creditLimit),
      canApproveCredit: hasPermission(Permissions.B2B_ORDERS_CREDIT_APPROVE),
      isGtPrepaid: isGtPrepaidOrder(row),
      canConfirmGtPrepaid: hasPermission(ORDERS_CONFIRM_GT_PREPAID),
      orderTotal: row.total_amount,
    });
    if (prompt.kind === 'blocked') {
      Modal.error({ title: prompt.title, content: prompt.content });
      return;
    }
    const isPickup = isPickupHandoverOnConfirm(row);
    const outstandingDebt = getOrderOutstandingDebt(row);
    let debtAcknowledged = false;
    Modal.confirm({
      title: prompt.title,
      content: <PickupOrderConfirmationContent
        prompt={prompt.content}
        isPickupHandoverOnConfirm={isPickup}
        outstandingDebt={outstandingDebt}
        onAcknowledge={(acknowledged) => { debtAcknowledged = acknowledged; }}
      />,
      okText: prompt.okText,
      cancelText: 'Thoát',
      onOk: () => {
        if (isPickup && !canConfirmPickupOrder(outstandingDebt, debtAcknowledged)) {
          message.warning('Xác nhận ô đồng ý ghi nhận công nợ trước khi Duyệt đơn và bàn giao.');
          return Promise.reject(new Error('Cần xác nhận công nợ'));
        }
        return handleConfirmOrder(row, debtAcknowledged);
      },
    });
  };

  /**
   * TCR-1094: "Đang đóng gói" tái dùng `useCreateOffPlatformExportSlips` —
   * hook này chỉ invalidate query key `['orders','offplatform-fulfillment']`
   * (trang Chuẩn bị hàng), KHÔNG invalidate `orderKeys.lists()` — phải tự
   * invalidate thêm để dòng trên `/orders` cập nhật `fulfillment_progress`
   * mới ngay, không cần refresh tay.
   */
  const handleAdvanceToPacking = (row: TenantOrder) => {
    Modal.confirm({
      title: `Chuyển đơn ${row.order_number} sang Đang đóng gói?`,
      content: 'Thao tác này sẽ tạo phiếu xuất kho cho đơn.',
      okText: 'Xác nhận',
      cancelText: 'Thoát',
      onOk: async () => {
        await advancePackingMutation.mutateAsync({ order_ids: [row._id] });
        void queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
      },
    });
  };

  /**
   * TCR-1094: "Đã gửi hàng" — chỉ gọi được cho đơn MANUAL (đã lọc ở orderActionColumn).
   * Final fix wave (Fix 2): `useMarkOffPlatformPickedUp` (hook dùng chung, KHÔNG sửa ở
   * đây) không có `onError` như 2 hook anh em cùng file — bọc try/catch tại call site
   * để lỗi (sai state, race condition) không bị nuốt im lặng (antd chỉ log lỗi
   * `onOk` ra console, dialog đóng mà người dùng không biết thao tác thất bại).
   */
  const handleMarkPickedUp = (row: TenantOrder) => {
    Modal.confirm({
      title: `Đánh dấu đơn ${row.order_number} đã gửi hàng?`,
      okText: 'Xác nhận',
      cancelText: 'Thoát',
      onOk: async () => {
        try {
          await markPickedUpMutation.mutateAsync(row._id);
          void queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
          message.success('Đã đánh dấu đơn đã gửi hàng');
        } catch (e: unknown) {
          message.error(getApiError(e)?.message ?? 'Đánh dấu đã gửi hàng thất bại');
        }
      },
    });
  };

  const handleToggleSkip = (row: TenantOrder) => {
    const mutation = row.skipped ? unskipMutation : skipMutation;
    const successLabel = row.skipped ? 'Đã bỏ đánh dấu bỏ qua' : 'Đã đánh dấu bỏ qua đơn';
    mutation.mutate(row._id, {
      onSuccess: () => message.success(successLabel),
      onError: () => message.error('Thao tác thất bại'),
    });
  };

  /** TCR-1963 — đơn Nháp → Chờ xác nhận, không còn bị tự huỷ. */
  const handleSubmitDraft = (row: TenantOrder) => {
    Modal.confirm({
      title: `Gửi xác nhận đơn nháp ${row.order_number}?`,
      content: 'Đơn chuyển sang "Chờ xác nhận" và không còn bị tự huỷ.',
      okText: 'Gửi xác nhận',
      cancelText: 'Thoát',
      onOk: async () => {
        try {
          await orderService.submitDraft(row._id);
          void queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
          message.success('Đã gửi xác nhận đơn nháp');
        } catch (e: unknown) {
          message.error(getApiError(e)?.message ?? 'Gửi xác nhận thất bại');
        }
      },
    });
  };

  /**
   * TCR-1963 — "Xoá hẳn" đơn Nháp: nhả tồn + xoá vĩnh viễn. BE có thể trả
   * `deleted: false` + `cancellation_pending: true` khi đơn đã huỷ (nhả tồn)
   * nhưng chưa xoá vì hoàn tác khuyến mại đang chạy dở — vẫn phải invalidate
   * list ở cả hai nhánh vì trạng thái đơn đã đổi (CANCELED, nhả tồn).
   */
  const handleDeleteDraft = (row: TenantOrder) => {
    Modal.confirm({
      title: `Xoá hẳn đơn nháp ${row.order_number}?`,
      content: 'Đơn bị xoá vĩnh viễn và tồn đang giữ được nhả ra. Không thể hoàn tác.',
      okText: 'Xoá hẳn',
      okButtonProps: { danger: true },
      cancelText: 'Thoát',
      onOk: async () => {
        try {
          const result = await orderService.deleteDraft(row._id);
          void queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
          if (result.deleted) {
            message.success('Đã xoá đơn nháp');
          } else if (result.cancellation_pending) {
            message.warning('Đã huỷ đơn nháp, chưa xoá hẳn vì đang hoàn tác khuyến mại');
          }
        } catch (e: unknown) {
          message.error(getApiError(e)?.message ?? 'Xoá đơn nháp thất bại');
        }
      },
    });
  };

  /**
   * TCR-1714 — 4 hành động (sao chép / đổi trả / gán thẻ / bỏ qua đơn) trước đây không gate
   * quyền nào: "Gán thẻ" và "Bỏ qua" đi ké `orders.update`, "Sao chép"/"Đổi trả" hoàn toàn tự
   * do. Giờ mỗi cái một key riêng.
   *
   * HIỆN nút rồi cảnh báo khi bấm (không ẩn hẳn như 3 nút in ở TCR-1710) — mô tả ticket yêu
   * cầu đích danh "nếu ko có quyền này thì khi click vào button sẽ hiển thị cảnh báo", cùng
   * cách đã làm cho nút "Huỷ" ở TCR-1709 (xem `handleCancelOrder`).
   */
  const requirePermission = (permission: string, label: string): boolean => {
    if (hasPermission(permission)) return true;
    message.error(`Bạn không có quyền ${label}. Vui lòng liên hệ với quản lý.`);
    return false;
  };

  /** TCR-2504 — bấm nút panel khi chọn đúng 1 đơn: luồng từng đơn (như menu ⋮ cũ của dòng đó). */
  const handleSingleOrderAction = (key: string, row: TenantOrder) => {
    if (key === 'copy') {
      if (requirePermission('orders.duplicate', 'sao chép đơn hàng')) void handleCopyOrder(row);
    }
    else if (key === 'return') {
      if (requirePermission('orders.return', 'đổi trả hàng')) handleReturnOrder();
    }
    else if (key === 'cancel') handleCancelOrder(row);
    else if (key === 'confirm') void confirmConfirmOrder(row);
    else if (key === 'advance-packing') handleAdvanceToPacking(row);
    else if (key === 'mark-picked-up') handleMarkPickedUp(row);
    else if (key === 'submit-draft') {
      if (requirePermission('pos.orders.create', 'gửi xác nhận đơn nháp')) handleSubmitDraft(row);
    }
    else if (key === 'delete-draft') {
      if (requirePermission('orders.cancel', 'xoá đơn nháp')) handleDeleteDraft(row);
    }
    else if (key === 'tags') {
      if (requirePermission('orders.tags.assign', 'gán thẻ đơn hàng')) setTagModalOrder(row);
    }
    else if (key === 'skip' || key === 'unskip') {
      if (requirePermission('orders.skip', 'bỏ qua đơn hàng')) handleToggleSkip(row);
    }
    else if (key === 'send:customer') setSendToCustomerOrder(row);
    else if (key === 'print:slip') void handlePrintSlip(row);
    else if (key.startsWith('print:')) {
      const kind = key.slice('print:'.length) as 'receipt' | 'invoice';
      handlePrintReceipt(row, kind);
    }
  };

  // Đếm từ danh sách tag, KHÔNG dùng activeFilterCount của useListPageState: hook đếm
  // filter key thô nên khoảng ngày cụ thể tính thành 2 (date_from + date_to) trong khi
  // màn hình chỉ hiện 1 tag "Thời gian" → "Xóa bộ lọc (3)" cạnh "Đang lọc (2)".
  const activeFilterCount = countActiveOrderFilters(filters, staffOptions);

  const buildOpts = { filters, onApplyFilter: applyFilterPatch, staffOptions, storeOptions };
  const primaryFilterConfigs = buildOrderPrimaryFilters(buildOpts);
  const advancedFilterConfigs = buildOrderAdvancedFilters(buildOpts);
  /**
   * TCR-1092 vòng 2: badge trên nút "Bộ lọc" đếm RIÊNG filter nằm trong drawer. Đếm cả
   * filter ngoài (search/kênh/thời gian) sẽ báo số ngay cả khi drawer trống trơn, người
   * dùng mở ra không thấy gì đang bật.
   */
  const advancedActiveCount = advancedFilterConfigs.filter(
    (f) => filters[f.key as OrderFilterKey],
  ).length;
  /**
   * Mobile: `FilterBar` không tự render hàng search/kênh/thời gian nữa (xem hàng 2 bên dưới),
   * nên các ô đó phải gộp vào CHUNG drawer "Bộ lọc" — nếu không người dùng mất luôn đường vào.
   * Badge trên nút đổi theo: mobile đếm TOÀN BỘ filter đang bật (khớp với những gì drawer
   * hiển thị), desktop vẫn chỉ đếm phần nâng cao vì search/kênh/thời gian đã lộ thiên ngoài.
   */
  const filterDrawerFilterConfigs = isMobileFilterLayout
    ? [...buildOrderPrimaryFilters({ ...buildOpts, stacked: true }), ...advancedFilterConfigs]
    : advancedFilterConfigs;
  const filterDrawerActiveCount = isMobileFilterLayout ? activeFilterCount : advancedActiveCount;
  const selectedRows = useMemo(
    () => effectiveSelection(selectionMemo, selectedRowKeys, rows, selectionScope),
    [selectionMemo, selectedRowKeys, rows, selectionScope],
  );
  const selectedCount = selectedRows.length;
  // TCR-2439 — hạn mức HIỆN TẠI của khách tổ chức có đơn MT/Sỉ đang chọn (cờ gắn lúc tạo đơn có thể
  // đã lỗi thời khi hạn mức được nâng/hạ sau đó) để panel tính lại theo tổng các đơn đang chọn.
  const creditCustomerIds = useMemo(() => creditCheckCustomerIds(selectedRows), [selectedRows]);
  const creditLimits = useB2BCreditLimitsOf(creditCustomerIds);
  const batchPlans = useMemo(() => planBatchOrderActions(selectedRows, {
    roles: userRolesOf(user),
    canOverrideLock: canOverrideLockOrders,
    canConfirmGtPrepaid: hasPermission(ORDERS_CONFIRM_GT_PREPAID),
    canApproveCredit: hasPermission(Permissions.B2B_ORDERS_CREDIT_APPROVE),
    canAssignTags: hasPermission('orders.tags.assign'),
    creditLimits,
  }), [selectedRows, user, canOverrideLockOrders, hasPermission, creditLimits]);
  const [batchBusy, setBatchBusy] = useState(false);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const assignTagsMutation = useAssignOrderTags();

  /** Báo tổng kết, làm mới danh sách, bỏ chọn đơn đã xử lý xong. */
  const finishBatch = (
    actionLabel: string,
    result: { succeeded: string[]; failures: { label: string; message: string }[] },
    skipped: string[] = [],
    { keepSelection = false }: { keepSelection?: boolean } = {},
  ) => {
    if (result.succeeded.length) message.success(`${actionLabel}: thành công ${result.succeeded.length} đơn`);
    if (result.failures.length) {
      Modal.warning({
        title: `${actionLabel}: thất bại ${result.failures.length} đơn`,
        content: <div>{formatBatchFailures(result.failures).map((line) => <div key={line}>{line}</div>)}</div>,
      });
    }
    if (skipped.length) message.info(`Bỏ qua ${skipped.length} đơn: ${skipped.join(', ')}`);
    if (!keepSelection) {
      const done = new Set(result.succeeded);
      setSelectedRowKeys(selectedRowKeys.filter((key) => !done.has(String(key))));
    }
    void queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
  };

  const excludedNote = (excluded: { order: TenantOrder; reason: string }[]) => excluded.length
    ? <div style={{ marginTop: 8, color: '#6b6b6b' }}>Không xử lý {excluded.length} đơn: {excluded.slice(0, 5).map((e) => `${e.order.order_number} (${e.reason})`).join('; ')}{excluded.length > 5 ? '…' : ''}</div>
    : null;

  const handleBatchConfirm = () => {
    const plan = batchPlans.confirm;
    if (!plan || plan.eligible.length === 0) return;
    const { debtOrders, totalDebt } = splitConfirmTargets(plan.eligible, false);
    const { creditOverLimit } = batchPlans;
    let debtAcknowledged = false;
    Modal.confirm({
      // TCR-2439 v3 — cùng tiêu đề với popup duyệt từng đơn vượt hạn mức.
      title: creditOverLimit.length > 0
        ? `Xác nhận duyệt ${plan.eligible.length} đơn hàng vượt hạn mức công nợ?`
        : `Xác nhận ${plan.eligible.length} đơn được chọn?`,
      width: creditOverLimit.length > 0 ? 560 : 480,
      content: (
        <BatchConfirmContent
          debtOrders={debtOrders}
          totalDebt={totalDebt}
          onAcknowledge={(acknowledged) => { debtAcknowledged = acknowledged; }}
          footer={excludedNote(plan.excluded)}
          creditOverLimit={creditOverLimit}
        />
      ),
      okText: creditOverLimit.length > 0 ? 'Duyệt đơn' : 'Xác nhận',
      cancelText: 'Thoát',
      onOk: async () => {
        const { toConfirm, skippedForDebt } = splitConfirmTargets(plan.eligible, debtAcknowledged);
        setBatchBusy(true);
        try {
          // Tuần tự từng đơn: Duyệt đơn nhận tại quầy kéo theo bàn giao và hạch toán, BE chạy song song
          // tranh khoá (0,3s → 2,2s/đơn khi kiểm chứng TCR-2264) — ưu tiên an toàn hơn tốc độ.
          const result = await runBatch(toConfirm, 1, (o) => o.order_number, (o) =>
            confirmMutation.mutateAsync(buildPickupOrderConfirmVariables(o, debtAcknowledged)));
          finishBatch('Xác nhận', result, skippedForDebt.map((o) => o.order_number));
        } finally {
          setBatchBusy(false);
        }
      },
    });
  };

  const handleBatchAdvancePacking = () => {
    const plan = batchPlans.advancePacking;
    if (!plan || plan.eligible.length === 0) return;
    Modal.confirm({
      title: `Chuyển ${plan.eligible.length} đơn sang Đang đóng gói?`,
      content: <div>Thao tác này sẽ tạo phiếu xuất kho cho các đơn.{excludedNote(plan.excluded)}</div>,
      okText: 'Xác nhận',
      cancelText: 'Thoát',
      onOk: async () => {
        setBatchBusy(true);
        try {
          // Gọi thẳng API (không qua hook dùng chung) để đọc `failed_count` và tránh toast trùng của hook.
          const ids = plan.eligible.map((o) => o._id);
          const result = await offPlatformFulfillmentApi.createExportSlips({ order_ids: ids });
          void queryClient.invalidateQueries({ queryKey: ['orders', 'offplatform-fulfillment'] });
          const [failure] = packingResultFailures(result);
          if (!failure) {
            finishBatch('Đang đóng gói', { succeeded: ids, failures: [] });
          } else {
            // BE không nêu đơn nào hỏng: báo số lượng, làm mới danh sách, bỏ chọn để người dùng chọn lại.
            if (result.success_count) message.success(`Đang đóng gói: thành công ${result.success_count} đơn`);
            Modal.warning({ title: `Đang đóng gói: ${failure.label} không chuyển được`, content: `${failure.message}. Danh sách đã được làm mới.` });
            clearSelection();
            void queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
          }
        } catch (e: unknown) {
          message.error(errorMessageOf(e, 'Chuyển Đang đóng gói thất bại'));
        } finally {
          setBatchBusy(false);
        }
      },
    });
  };

  const handleBatchSkip = () => {
    const plan = batchPlans.skip;
    if (plan.eligible.length === 0) return;
    Modal.confirm({
      title: `Đánh dấu bỏ qua ${plan.eligible.length} đơn?`,
      content: excludedNote(plan.excluded),
      okText: 'Bỏ qua',
      cancelText: 'Thoát',
      onOk: async () => {
        setBatchBusy(true);
        try {
          finishBatch('Bỏ qua', await runBatch(plan.eligible, 5, (o) => o.order_number, (o) => skipMutation.mutateAsync(o._id)));
        } finally {
          setBatchBusy(false);
        }
      },
    });
  };

  const handleBulkTagSubmit = async (tagIds: string[]) => {
    const plan = batchPlans.tags;
    if (!plan) return;
    setBatchBusy(true);
    try {
      const result = await runBatch(plan.eligible, 5, (o) => o.order_number, (o) =>
        assignTagsMutation.mutateAsync({ orderId: o._id, add_tag_ids: tagIds, remove_tag_ids: [] }));
      setBulkTagOpen(false);
      finishBatch('Gán thẻ', result);
    } finally {
      setBatchBusy(false);
    }
  };


  const rowMenuExtras = useMemo(() => planRowMenuExtras(selectedRows, userRolesOf(user)), [selectedRows, user]);
  const panelContext: OrderPanelContext = {
    roles: userRolesOf(user),
    printPermissions: {
      canPrintSlip: hasPermission('orders.print.slip'),
      canPrintReceipt: hasPermission('orders.print.receipt'),
      canPrintInvoice: hasPermission('orders.print.invoice'),
    },
    canOverrideLock: canOverrideLockOrders,
    canConfirmGtPrepaid: hasPermission(ORDERS_CONFIRM_GT_PREPAID),
  };
  const panelSections = selectedRows.length === 1
    ? buildSingleOrderSections(selectedRows[0], panelContext)
    : buildMultiOrderSections(selectedRows, batchPlans, rowMenuExtras, panelContext);

  /** Hộp xác nhận + chạy từng đơn + tổng kết — cho các mục menu ⋮ mà panel hàng loạt chưa có. */
  const confirmRowMenuBatch = (opts: {
    plan: BatchActionPlan;
    title: string;
    note?: string;
    okText: string;
    danger?: boolean;
    actionLabel: string;
    limit: number;
    run: (order: TenantOrder) => Promise<unknown>;
    keepSelection?: boolean;
  }) => {
    if (opts.plan.eligible.length === 0) return;
    Modal.confirm({
      title: opts.title,
      content: <div>{opts.note}{excludedNote(opts.plan.excluded)}</div>,
      okText: opts.okText,
      okButtonProps: opts.danger ? { danger: true } : undefined,
      cancelText: 'Thoát',
      onOk: async () => {
        setBatchBusy(true);
        try {
          const result = await runBatch(opts.plan.eligible, opts.limit, (o) => o.order_number, opts.run);
          finishBatch(opts.actionLabel, result, [], { keepSelection: opts.keepSelection });
        } finally {
          setBatchBusy(false);
        }
      },
    });
  };

  /** TCR-2372 — Nháp → Chờ xác nhận cho nhiều đơn: nút trên panel hàng loạt và menu ⋮ dùng chung. */
  const handleBatchSubmitDraft = (plan: BatchActionPlan | null) => {
    if (!plan || !requirePermission('pos.orders.create', 'gửi xác nhận đơn nháp')) return;
    confirmRowMenuBatch({
      plan, title: `Gửi xác nhận ${plan.eligible.length} đơn nháp?`, note: 'Các đơn chuyển sang "Chờ xác nhận" và không còn bị tự huỷ.',
      okText: 'Gửi xác nhận', actionLabel: 'Gửi xác nhận', limit: 5,
      run: (o) => orderService.submitDraft(o._id),
    });
  };

  /**
   * TCR-2264/2504 — bấm nút panel khi chọn từ 2 đơn: áp cho mọi đơn đủ điều kiện, kiểm quyền giống
   * hệt bản từng đơn. Hành động chỉ cho 1 đơn (Sao chép/Đổi trả/In phiếu/Gửi cho khách) bị mờ sẵn
   * trên panel nên không tới đây.
   */
  const handleMultiOrderAction = (key: string) => {
    const n = (plan: BatchActionPlan) => plan.eligible.length;
    switch (key) {
      case 'cancel':
        handleBulkCancel();
        return;
      case 'confirm':
        // Đơn GT trả trước / vượt hạn mức đã bị loại khỏi kế hoạch (lý do ở tooltip) — duyệt riêng từng đơn.
        handleBatchConfirm();
        return;
      case 'advance-packing':
        handleBatchAdvancePacking();
        return;
      case 'mark-picked-up': {
        const plan = rowMenuExtras.markPickedUp;
        if (plan) {
          confirmRowMenuBatch({
            plan, title: `Đánh dấu ${n(plan)} đơn đã gửi hàng?`, okText: 'Xác nhận', actionLabel: 'Đã gửi hàng', limit: 5,
            run: (o) => markPickedUpMutation.mutateAsync(o._id),
          });
        }
        return;
      }
      case 'submit-draft':
        handleBatchSubmitDraft(batchPlans.submitDraft);
        return;
      case 'delete-draft': {
        const plan = rowMenuExtras.draft;
        if (plan && requirePermission('orders.cancel', 'xoá đơn nháp')) {
          confirmRowMenuBatch({
            plan, title: `Xoá hẳn ${n(plan)} đơn nháp?`, note: 'Các đơn bị xoá vĩnh viễn và tồn đang giữ được nhả ra. Không thể hoàn tác.',
            okText: 'Xoá hẳn', danger: true, actionLabel: 'Xoá hẳn', limit: 1,
            run: (o) => orderService.deleteDraft(o._id),
          });
        }
        return;
      }
      case 'tags':
        if (requirePermission('orders.tags.assign', 'gán thẻ đơn hàng')) setBulkTagOpen(true);
        return;
      case 'skip':
        if (requirePermission('orders.skip', 'bỏ qua đơn hàng')) handleBatchSkip();
        return;
      case 'unskip':
        if (requirePermission('orders.skip', 'bỏ qua đơn hàng')) {
          confirmRowMenuBatch({
            plan: rowMenuExtras.unskip, title: `Bỏ đánh dấu bỏ qua ${n(rowMenuExtras.unskip)} đơn?`,
            okText: 'Xác nhận', actionLabel: 'Bỏ đánh dấu bỏ qua', limit: 5,
            run: (o) => unskipMutation.mutateAsync(o._id),
          });
        }
        return;
      case 'print:receipt':
      case 'print:invoice': {
        const kind = key === 'print:receipt' ? 'receipt' : 'invoice';
        const label = kind === 'receipt' ? 'In hoá đơn POS' : 'In hoá đơn bán hàng';
        const plan = rowMenuExtras.print;
        // Lần lượt từng đơn (mỗi đơn một lệnh in) — đã chốt với người dùng; giữ vùng chọn sau khi in.
        confirmRowMenuBatch({
          plan, title: `${label} cho ${n(plan)} đơn?`, note: 'Mỗi đơn một lệnh in, lần lượt theo thứ tự đã chọn.',
          okText: 'In', actionLabel: label, limit: 1, keepSelection: true,
          run: async (o) => {
            const res = await printOrderReceipt(o, { storeName: o.store?.name, storeAddress: o.store?.address }, kind);
            if (!res.ok) throw new Error(`${label} thất bại`);
          },
        });
        return;
      }
    }
  };

  const orderCodeTitle = selectedCount > 0 ? (
    <OrderCodeSelectionHeader
      selectedCount={selectedCount}
      panel={(
        <PosBatchActionPanel
          selectedCount={selectedCount}
          sections={panelSections}
          busy={batchBusy}
          onAction={(key) => (selectedRows.length === 1 ? handleSingleOrderAction(key, selectedRows[0]) : handleMultiOrderAction(key))}
          onClear={clearSelection}
        />
      )}
    />
  ) : undefined;

  /** Bấm mã đơn để mở popup xem nhanh trước khi vào chi tiết hoặc chỉnh sửa. */
  const handleOpenSummary = useCallback((row: TenantOrder) => setSummaryOrderId(row._id), []);
  const handleOpenErpSkuStock = useCallback((sku: string, row: TenantOrder) => {
    const warehouseIds = (row.items ?? [])
      .filter((item) => item.sku.trim() === sku && item.warehouse_id)
      .map((item) => item.warehouse_id as string);
    setErpSkuStock({ sku, warehouseIds: [...new Set(warehouseIds)] });
  }, []);

  const orderColumns = useOrderColumns({
    filters,
    onApplyFilter: applyFilterPatch,
    sort: activeSort,
    onOpenSummary: handleOpenSummary,
    orderCodeTitle,
    onOpenErpSkuStock: handleOpenErpSkuStock,
  });

  /**
   * Sắp xếp đi qua URL (`sortField`/`sortOrder` của `useListPageState`) rồi mới xuống API,
   * nên thứ tự giữ nguyên khi chia sẻ link hoặc quay lại từ màn chi tiết.
   * Chỉ phản ứng với `action === 'sort'`: onChange còn bắn khi phân trang/lọc, xử lý cả
   * những lần đó sẽ ghi đè `page` giữa chừng.
   */
  const handleTableChange: NonNullable<TableProps<TenantOrder>['onChange']> = (
    _pagination,
    _tableFilters,
    sorter,
    extra,
  ) => {
    if (extra.action !== 'sort') return;
    const selected = Array.isArray(sorter) ? sorter[0] : sorter;
    setTableState({
      page: 1,
      sortField: selected?.order ? String(selected.columnKey) : '',
      sortOrder: selected?.order ?? null,
    });
  };

  const statusGroupCounts = data?.summary?.count_by_status_group;
  const statusItems = useMemo(
    () =>
      ORDER_STATUS_GROUP_OPTIONS.map((opt) => {
        const count = statusGroupCounts?.[opt.value] ?? 0;
        return { key: opt.value, label: buildOrderStatusTabLabel(opt.value, opt.label, count) };
      }),
    [statusGroupCounts],
  );

  return (
    // TCR-2491 — bỏ hàng tiêu đề "Đơn hàng (N)" để dành chỗ cho bảng: tổng số đơn vẫn có ở
    // badge tab "Tất cả" và chân phân trang. Bố cục bám màn Đơn ECom: nút + bộ lọc cỡ small
    // nằm TRÊN tabs trạng thái, bảng nén padding/cỡ chữ.
    <PageShell hideHeader fullHeight>
      <style>{ORDERS_LIST_CSS}</style>
      <ConfigProvider componentSize="small">
        <div className="orders-list-toolbar">
          <div className="orders-list-toolbar__main">
            <Button type="primary" icon={<ReloadOutlined />} loading={isFetching} onClick={() => void refetch()}>
              Refresh
            </Button>
            <Badge count={filterDrawerActiveCount} size="small" offset={[-4, 2]}>
              <Button
                icon={<FilterOutlined />}
                data-testid="orders-open-filter-drawer"
                onClick={() => setFilterDrawerOpen(true)}
              >
                Bộ lọc
              </Button>
            </Badge>
            <Tooltip title="Tuỳ chỉnh cột">
              <Button
                aria-label="Tuỳ chỉnh cột"
                icon={<SettingOutlined />}
                onClick={() => openColumnSettings?.()}
              >
                Cột
              </Button>
            </Tooltip>
            {/* TCR-1788 — tester yêu cầu ẩn nút "Tổng kết ca": tổng kết ca đã có chỗ riêng ở
                POS → Ca làm việc (mở/đóng ca, `ShiftDetailDrawer`), nút này chỉ là bản rút gọn
                trùng chức năng. Giữ nguyên Drawer + `OrderShiftSummary` bên dưới và ẩn bằng cờ
                `SHOW_SHIFT_SUMMARY_BUTTON` để bật lại chỉ cần đổi hằng số, không phải dựng lại
                UI (tránh comment-out code / import thừa theo quy ước repo). */}
            {SHOW_SHIFT_SUMMARY_BUTTON && (
              <Button icon={<BarChartOutlined />} onClick={() => setShiftSummaryOpen(true)}>
                Tổng kết ca
              </Button>
            )}
            {/* Ô tìm kiếm / kênh / thời gian + nút xoá, chung hàng với các nút. Không truyền
                onApply: instant-apply nên nút "Áp dụng" thừa.
                Mobile: ẩn hẳn — search/kênh/thời gian đã gộp vào chung drawer "Bộ lọc"
                (`filterDrawerFilterConfigs`), tránh 2 nút lọc ("Bộ lọc" + "Lọc" do FilterBar
                tự collapse) đứng cạnh nhau trên màn hẹp.
                TCR-2491: bỏ dòng "Đang lọc" (ActiveFilterTags) như màn ECom — badge nút "Bộ lọc"
                và nút "Xóa bộ lọc (N)" đã báo đang lọc. */}
            {!isMobileFilterLayout && (
              <div className="orders-list-toolbar__filters">
                <FilterBar
                  filters={primaryFilterConfigs}
                  activeCount={activeFilterCount}
                  onClear={clearFilters}
                />
              </div>
            )}
          </div>
          {/* TCR-1546: "Nhập từ file Excel" nằm cạnh "+ Tạo đơn" để team B2B (lên đơn hàng
              loạt) thấy ngay. TCR-2491: header đã bỏ nên cụm này ghim góc phải hàng công cụ,
              như "Xuất excel" bên ECom.
              TCR-1626/1627 — gate nhập Excel theo `orders.import` nằm TRONG dropdown
              (`canImport`), không bọc cả nút: "Xuất Excel" vốn không bị chặn. */}
          <Space size={6} style={{ flexShrink: 0 }}>
            <OrderExcelButton
              canImport={hasPermission('orders.import')}
              canExport={hasPermission('orders.export')}
              onOpenHistory={() => navigate(ROUTES.orders.imports)}
              onOpenImport={() => setImportOpen(true)}
              filter={{
                channel_group: filters.channel_group,
                status: filters.status,
                status_group: filters.status_group,
                search: filters.search,
                order_number: filters.order_number,
                customer: filters.customer,
                created_at_from: apiDateFrom,
                created_at_to: apiDateTo,
                total_from: parseOrderAmount(filters.total_from),
                total_to: parseOrderAmount(filters.total_to),
                // Xuất Excel phải theo ĐÚNG bộ lọc đang hiển thị, kể cả các filter nằm
                // trong drawer (xem `tcr1092ApiFilters`).
                ...tcr1092ApiFilters,
              }}
            />
            {canCreateOrder && (
              <Button type="primary" onClick={() => navigate(ROUTES.pos)}>+ Tạo đơn</Button>
            )}
          </Space>
        </div>
      </ConfigProvider>
      <Tabs
        className="orders-list-tabs"
        size="small"
        activeKey={filters.status_group || 'all'}
        items={statusItems}
        onChange={(key) => applyFilterPatch({ status_group: key === 'all' ? '' : key })}
      />
      <ERPTable<TenantOrder>
        tableKey={pageKey}
        className="orders-list-table"
        showSorterTooltip={false}
        columns={orderColumns}
        fitHeight
        bottomOffset={16}
        hideSettingsButton
        registerOpenSettings={(fn) => setOpenColumnSettings(() => fn)}
        dataSource={rows}
        loading={isFetching}
        onChange={handleTableChange}
        rowKey="_id"
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
          preserveSelectedRowKeys: true,
        }}
        size="small"
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `${t} đơn hàng`,
          onChange: (p, ps) => setTableState({ page: p, pageSize: ps }),
        }}
      />
      <OrderFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={filterDrawerFilterConfigs}
        activeCount={filterDrawerActiveCount}
        onClear={clearFilters}
      />
      <Drawer
        title="Tổng kết ca hiện tại"
        placement="right"
        open={shiftSummaryOpen}
        onClose={() => setShiftSummaryOpen(false)}
        width={520}
        styles={{ body: { padding: 16 } }}
      >
        <OrderShiftSummary totalOrders={total} summary={data?.summary} />
      </Drawer>
      {/* Chỉ dựng khi có đơn đang xem: modal gọi useOrderDetail, không cần mount trên mọi render. */}
      {summaryOrderId && (
        <OrderSummaryModal
          orderId={summaryOrderId}
          onClose={() => setSummaryOrderId(null)}
          onViewDetail={(id) => navigate(ROUTES.orders.detail(id))}
          onEditOrder={(id) => navigate(`/orders/${id}/edit`)}
        />
      )}
      <PosErpSkuStockModal
        sku={erpSkuStock?.sku ?? null}
        orderWarehouseIds={erpSkuStock?.warehouseIds}
        onClose={() => setErpSkuStock(null)}
      />
      <OrderTagModal order={tagModalOrder} onClose={() => setTagModalOrder(null)} />
      <BulkOrderTagModal
        open={bulkTagOpen}
        orderCount={batchPlans.tags?.eligible.length ?? 0}
        submitting={batchBusy}
        onSubmit={(tagIds) => void handleBulkTagSubmit(tagIds)}
        onClose={() => setBulkTagOpen(false)}
      />
      <SendToCustomerModal order={sendToCustomerOrder} onClose={() => setSendToCustomerOrder(null)} />
      <ImportOrderModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => void refetch()}
      />
    </PageShell>
  );
}
